import dcopy from 'deep-copy';
import { action, computed, observable, runInAction } from 'mobx';
import React from 'react';
import type { SortingObservable } from '../../components/Grid/Grid';
import { GridColDef } from '../../components/Grid/Grid';
import { checkSinglePermission } from '../../security/security-hook';
import AbstractStore from '../../store/AbstractStore';
import { USERS_PATH } from '../../store/constants';
import {
  PERMISSION_ALLOW,
  RESOURCE_USER_MANAGEMENT_BLOCK_USER,
  RoleApi,
  SapRole,
  UserApi,
} from '../../types/users-types';
import { toUtcFormattedDate } from '../../utils/date-utils';
import { isDefined, isIdexxEmailAddress, sortByField } from '../../utils/object-utils';
import { regexToSearchWithWildcards } from '../../utils/search-utils';
import AddSapContent from './AddSapContent';
import AddUsersContent from './AddUsersContent';
import {
  ActionsRenderer,
  BlockedRenderer,
  expandCollapseRenderer,
  ManagedUser,
  RoleIndicesPerSapId,
  rowCellRenderer,
  SapRenderer,
  subrowActionsRenderer,
  subrowRolesRenderer,
} from './renderers';

interface SapRolesObject {
  sapId: string;
  roleNames?: string[];
}

export const emptyUser = (): ManagedUser => ({
  firstName: '',
  lastName: '',
  email: '@idexx.com',
  phone: '',
  blocked: false,
  roleIndices: [],
  sapRoleIndices: {},
});

const isEmptyUser = (user: ManagedUser): boolean =>
  Object.keys(user)
    .filter(key => key !== 'roleIndices' && key !== 'sapRoleIndices')
    .every(key => !user[key]);

const equalUser = (user1: ManagedUser, user2: ManagedUser): boolean =>
  Object.keys(user1)
    .filter(key => !['roleIndices', 'sapRoleIndices'].includes(key))
    .every(key => user1[key] === user2[key]);

// const MIN_GUEST_ROLE_LEVEL = 9000;

interface Loaders {
  page?: boolean;
  userAction?: boolean;
}

export class UsersManagementStore extends AbstractStore {
  @observable practiceUsersMode = false;

  @observable loaders: Loaders = { page: true };

  @observable idexxUsers: ManagedUser[] = [];

  @observable practiceUsers: ManagedUser[] = [];

  @observable roles: RoleApi[] = [];

  @observable error: string | null = null;

  @observable currentUserError = emptyUser();

  @observable currentUserIndex: number | undefined = undefined;

  @observable currentUser = emptyUser();

  @observable currentUserRolesChanged = false;

  @observable hideGuests = true;

  @observable searchText = '';

  @observable sorting: SortingObservable<ManagedUser> = {};

  @observable expandedRowIds: string[] = [];

  blockingUsersEnabled = false;

  @computed
  public get users(): ManagedUser[] {
    return this.practiceUsersMode ? this.practiceUsers : this.idexxUsers;
  }

  @computed
  public get globalRoles(): RoleApi[] {
    const currentUserRoleLowestLevel = this.root.apiStore.userGlobalRoleLowestLevel;
    return this.roles.filter(r => r.global && !r.hidden && r.level >= currentUserRoleLowestLevel);
  }

  @computed
  public get nonGlobalRoles(): RoleApi[] {
    return this.roles.filter(r => !r.global && !r.hidden);
  }

  @computed
  public get filteredUsers(): ManagedUser[] {
    let filtered = this.users.filter(u => !isEmptyUser(u));
    if (filtered.length > 0) {
      if (this.hideGuests && !this.practiceUsersMode) {
        filtered = filtered.filter(u => isEmptyUser(u) || u.roleIndices.length > 0);
      }
      if (this.searchText !== '') {
        const regex = regexToSearchWithWildcards(this.searchText);
        filtered = filtered.filter(
          u =>
            isEmptyUser(u) ||
            regex.test(u.email) ||
            regex.test(u.firstName || '') ||
            regex.test(u.lastName || ''),
        );
      }
      if (this.sorting.field) {
        filtered = sortByField(filtered, this.sorting.field);
        if (this.sorting.order === 'DESC') {
          filtered = filtered.reverse();
        }
      } else {
        filtered = this.applyDefaultSorting(filtered);
      }
    }
    return filtered;
  }

  @computed
  public get initialUser(): ManagedUser | undefined {
    if (!isDefined(this.currentUser)) return undefined;
    return this.users.find(u => u.email === this.currentUser.email);
  }

  @computed
  public get colDefs(): GridColDef<ManagedUser>[] {
    return [
      {
        headerName: '',
        field: 'expand',
        renderer: this.practiceUsersMode
          ? expandCollapseRenderer(this.switchExpandedRow.bind(this))
          : undefined,
        renderValue: () => '',
        sortable: false,
      },
      {
        headerName: 'First Name',
        field: 'firstName',
        renderer: rowCellRenderer('firstName'),
        sortable: true,
      },
      {
        headerName: 'Last Name',
        field: 'lastName',
        renderer: rowCellRenderer('lastName'),
        sortable: true,
      },
      { headerName: 'Email', field: 'email', renderer: rowCellRenderer('email'), sortable: true },
      { headerName: 'Phone (mobile)', field: 'phone', renderer: rowCellRenderer('phone') },
      this.practiceUsersMode
        ? { headerName: 'SAPs', field: 'sapRoles', renderer: SapRenderer }
        : { headerName: 'Roles', field: 'roles', renderer: rowCellRenderer('roleIndices') },
      { headerName: 'Blocked', field: 'blocked', renderer: BlockedRenderer, sortable: true },
      { headerName: 'Last Access (UTC)', field: 'lastAccessAt' },
      { headerName: 'Created (UTC)', field: 'createdAt' },
      { headerName: 'Actions', field: 'actions', renderer: ActionsRenderer },
    ];
  }

  @action
  public async init(): Promise<void> {
    this.cleanCurrentUser();
    runInAction(() => (this.loaders.page = true));
    const roles = await this.root.apiStore.findAllRoles();
    runInAction(() => {
      if (roles.length > 0) {
        this.roles = roles.sort(r => r.level);
      }
      this.searchText = '';
      this.hideGuests = true;
      this.sorting = {};
      this.practiceUsersMode = false;
      this.practiceUsers = [];
      this.blockingUsersEnabled = checkSinglePermission(
        this.root.apiStore.userGlobalPermissions,
        RESOURCE_USER_MANAGEMENT_BLOCK_USER,
        PERMISSION_ALLOW,
      );
    });
    runInAction(() => (this.loaders.page = false));
    await this.fetchUsers();
  }

  @action
  public async fetchUsers(): Promise<void> {
    runInAction(() => (this.loaders.page = true));
    const currentUserRoleLowestLevel = this.root.apiStore.userGlobalRoleLowestLevel;
    if (currentUserRoleLowestLevel === Infinity) {
      runInAction(() => (this.idexxUsers = []));
    } else {
      const users = await this.findUsersAllowedToBeManagedByCurrentUser(currentUserRoleLowestLevel);
      runInAction(() => {
        this.idexxUsers = users.map(u => this.userApiToManagedUser(u));
      });
    }
    runInAction(() => (this.loaders.page = false));
  }

  @action
  public async fetchNonIdexxUsers(): Promise<void> {
    runInAction(() => (this.loaders.page = true));
    const users = await this.root.apiStore.findAllUsers();
    runInAction(() => {
      this.practiceUsers = users
        .filter(u => !isIdexxEmailAddress(u.email))
        .map(u => this.userApiToManagedUser(u));
      this.loaders.page = false;
    });
  }

  @action
  public async togglePracticeUsersMode(): Promise<void> {
    runInAction(() => {
      this.expandedRowIds = [];
      this.practiceUsersMode = !this.practiceUsersMode;
    });
    if (this.practiceUsersMode && this.practiceUsers.length === 0) {
      await this.fetchNonIdexxUsers();
      runInAction(() => (this.hideGuests = false));
    }
  }

  @action
  public editUser(index: number): void {
    this.currentUserIndex = index;
    this.currentUser = dcopy(this.filteredUsers[index]);
    this.currentUserError = emptyUser();
    if (this.practiceUsersMode) {
      if (!this.expandedRowIds.includes(this.currentUser.email)) {
        this.expandedRowIds.push(this.currentUser.email);
      }
    }
    this.currentUserRolesChanged = false;
    void this.audit('USER_MANAGEMENT', 'USER_MANAGEMENT_PAGE_EDIT_USER');
  }

  @action
  public showAddSapModal(): void {
    this.root.globalStore.showOkCancelPopup(
      'Enter SAP ID',
      React.createElement(AddSapContent),
      'Submit',
      'Cancel',
      () => {},
    );
  }

  @action
  public addSap(sapId: string): void {
    const roleIndex = this.nonGlobalRoles.findIndex(r => r.default);
    this.currentUser.sapRoleIndices[sapId] = [roleIndex];
    this.currentUserRolesChanged = true;
  }

  @action
  public showDeleteSapModal(sapId: string): void {
    this.root.globalStore.showOkCancelPopup(
      'Confirm SAP deletion',
      'Are you sure you want to delete this SAP?',
      'OK',
      'Cancel',
      () => this.deleteSap(sapId),
    );
  }

  @action
  public deleteSap(sapId: string): void {
    delete this.currentUser.sapRoleIndices[sapId];
    this.currentUserRolesChanged = true;
  }

  @action
  public deleteUser(email: string): void {
    if (this.loaders.userAction) return;
    this.root.globalStore.showOkCancelPopup(
      'Confirm user deletion',
      'Are you sure you want to delete this user?',
      'OK',
      'Cancel',
      () => this.doDeleteUser(email),
    );
  }

  @action
  public cancelEditingUser(): void {
    this.cleanCurrentUser();
    runInAction(() => (this.loaders.userAction = false));
  }

  @action
  public async validateAndUpdateUser(): Promise<void> {
    runInAction(() => {
      const { firstName, lastName, phone, email } = this.currentUser;
      this.currentUserError = emptyUser();
      this.currentUserError.email = '';
      if (!firstName) this.currentUserError.firstName = 'First name should be set';
      if (!lastName) this.currentUserError.lastName = 'Last name should be set';
      if (email) {
        if (!this.practiceUsersMode && !isIdexxEmailAddress(email)) {
          this.currentUserError.email = 'Invalid email format';
        }
      } else {
        this.currentUserError.email = 'Email should be set';
      }
      if (phone && !/^\d{10}$/.test(phone)) this.currentUserError.phone = 'Invalid phone format';
    });
    if (isEmptyUser(this.currentUserError)) {
      if (!equalUser(this.currentUser, this.initialUser!) || this.currentUserRolesChanged) {
        await this.updateUser();
      }
      this.cleanCurrentUser();
    }
  }

  @action
  public changeRoleIndices(editUserRoleIndices: number[]): void {
    const initialUserRoleIndices: number[] = this.initialUser!.roleIndices;
    const isSameArrays =
      initialUserRoleIndices.length === editUserRoleIndices.length &&
      initialUserRoleIndices.every(item => editUserRoleIndices.includes(item)) &&
      editUserRoleIndices.every(item => initialUserRoleIndices.includes(item));
    this.currentUserRolesChanged = !isSameArrays;
    this.currentUser.roleIndices = editUserRoleIndices;
  }

  @action
  public changePerSapRoleIndices(sapId: string, editUserRoleIndices: number[]): void {
    this.currentUserRolesChanged = true;
    this.currentUser.sapRoleIndices[sapId] = editUserRoleIndices;
  }

  @action
  public addUsersModal(): void {
    this.root.globalStore.showOkCancelPopup(
      'Add Users',
      React.createElement(AddUsersContent),
      '',
      'Cancel',
      () => {},
    );
  }

  @action.bound
  public switchExpandedRow(row: ManagedUser): void {
    if (this.expandedRowIds.includes(row.email)) {
      if (!this.currentUser || this.currentUser.email !== row.email) {
        this.expandedRowIds = this.expandedRowIds.filter(id => id !== row.email);
      }
    } else {
      this.expandedRowIds = [...this.expandedRowIds, row.email];
    }
  }

  @action
  public back(): void {
    void this.audit('USER_MANAGEMENT', 'USER_MANAGEMENT_PAGE_BACK');
    this.navigate(-1);
  }

  @action
  public done(): void {
    void this.audit('USER_MANAGEMENT', 'USER_MANAGEMENT_PAGE_DONE');
    this.navigate('/');
  }

  @action
  private async updateUser(): Promise<void> {
    runInAction(() => {
      this.loaders.userAction = true;
    });
    const { firstName, lastName, phone, email, blocked } = this.currentUser;
    try {
      const userRequest = {
        action: this.practiceUsersMode ? 'UPDATE_FOR_SAP' : 'UPDATE',
        email: email.toLowerCase(),
        firstName,
        lastName,
        phone,
        blocked: this.practiceUsersMode ? undefined : blocked,
        roleNames: !this.practiceUsersMode
          ? this.currentUser.roleIndices.map(i => this.globalRoles[i].name)
          : undefined,
        sapRoles: this.practiceUsersMode
          ? this.sapRolesFromManagedUser(this.currentUser, this.initialUser!)
          : undefined,
        forceRoles: this.practiceUsersMode,
      };
      void this.audit('USER_MANAGEMENT', `USER_MANAGEMENT_PAGE_${userRequest.action}_USER`);
      const userResponse = await this.root.apiStore.backendPost(USERS_PATH, userRequest);
      const managedUserFromResponse = this.userApiToManagedUser(userResponse);
      runInAction(() => {
        if (this.practiceUsersMode) {
          const userIndex = this.practiceUsers.findIndex(u => u.email === email);
          this.practiceUsers[userIndex] = managedUserFromResponse;
        } else {
          const userIndex = this.idexxUsers.findIndex(u => u.email === email);
          this.idexxUsers[userIndex] = managedUserFromResponse;
        }
      });
    } finally {
      runInAction(() => {
        this.loaders.userAction = false;
      });
    }
  }

  @action
  private cleanCurrentUser(): void {
    this.currentUserIndex = undefined;
    this.currentUser = emptyUser();
    this.currentUserError = emptyUser();
  }

  public subRowColDefs(userEmail: string): GridColDef<RoleIndicesPerSapId>[] {
    return [
      { headerName: 'SAP ID', field: 'sapId' },
      { headerName: 'Roles', field: 'roles', renderer: subrowRolesRenderer(userEmail) },
      { headerName: 'Actions', field: '', renderer: subrowActionsRenderer(userEmail) },
    ];
  }

  private async findUsersAllowedToBeManagedByCurrentUser(
    currentUserRoleLowestLevel: number | undefined,
  ): Promise<UserApi[]> {
    const users = await this.root.apiStore.findUsersByLowestRoleLevel(currentUserRoleLowestLevel);
    return this.blockingUsersEnabled ? users : users.filter(u => !u.blocked);
  }

  private async doDeleteUser(email: string): Promise<void> {
    if (this.loaders.userAction) return;
    runInAction(() => (this.loaders.userAction = true));
    try {
      void this.audit('USER_MANAGEMENT', 'USER_MANAGEMENT_PAGE_DELETE_USER');
      await this.root.apiStore.backendPost(USERS_PATH, { action: 'DELETE', email });
      if (this.practiceUsersMode) {
        runInAction(() => (this.practiceUsers = this.practiceUsers.filter(u => u.email !== email)));
      } else {
        runInAction(() => (this.idexxUsers = this.idexxUsers.filter(u => u.email !== email)));
      }
    } catch {
      // ignore
    }
    this.cleanCurrentUser();
    runInAction(() => (this.loaders.userAction = false));
  }

  private applyDefaultSorting(users: ManagedUser[]): ManagedUser[] {
    return [...users].sort(
      (u1, u2) =>
        (u1.lastName || '').localeCompare(u2.lastName || '') ||
        (u1.firstName || '').localeCompare(u2.firstName || ''),
    );
  }

  private userApiToManagedUser(user: UserApi): ManagedUser {
    const { firstName, lastName, email, phone, blocked } = user;
    return {
      firstName,
      lastName,
      email,
      phone,
      blocked,
      roleIndices: this.rolesToRoleIndices(user.globalRoles),
      sapRoleIndices: this.rolesToSapRoleIndices(user.sapRoles),
      lastAccessAt: toUtcFormattedDate(user.lastAccessAt),
      createdAt: toUtcFormattedDate(user.createdAt),
    };
  }

  private rolesToRoleIndices(roles?: RoleApi[] | null): number[] {
    return (
      roles?.map(r => this.globalRoles.findIndex(gr => gr.name === r.name)).filter(i => i > -1) ||
      []
    );
  }

  private rolesToSapRoleIndices(sapRoles?: SapRole[] | null): Record<string, number[]> {
    const result: Record<string, number[]> = {};
    if (isDefined(sapRoles)) {
      sapRoles.forEach(sr => {
        result[sr.sapId] = sr.roles
          .map(r => this.nonGlobalRoles.findIndex(ngr => ngr.name === r.name))
          .filter(i => i > -1);
      });
    }
    return result;
  }

  private sapRolesFromManagedUser(user: ManagedUser, originalUser: ManagedUser): SapRolesObject[] {
    const result: SapRolesObject[] = Object.keys(user.sapRoleIndices).map(sapId => ({
      sapId,
      roleNames: user.sapRoleIndices[sapId].map(i => this.nonGlobalRoles[i].name),
    }));
    const absentSapIds = Object.keys(originalUser.sapRoleIndices).filter(
      sapId => !isDefined(user.sapRoleIndices[sapId]),
    );
    absentSapIds.forEach(sapId => {
      result.push({ sapId, roleNames: [] });
    });
    return result;
  }
}
